type Schedule = {
  day_of_week: (string | number)[];
  start_time: string | null;
  end_time: string | null;
};

type ProductSchedule = {
  id: number;
  product: number[];
  schedule: Schedule[];
};

const isValidTime = (time: string | null): boolean => {
  return time !== null && time !== ":00" && !time.includes("-");
};

const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":".slice(0, 2)).map(Number);
  return hours * 60 + minutes;
};

/**
 * The rule the editor has always blocked on: rows sharing a weekday whose
 * windows overlap on that day (an overnight end just gets +24h). It never
 * looks at the after-midnight half landing on the next day.
 */
const sameDayOverlap = (a: Schedule, b: Schedule): boolean => {
  const daysB = b.day_of_week.map(String);
  if (!a.day_of_week.map(String).some((day) => daysB.includes(day))) {
    return false;
  }
  const startA = timeToMinutes(a.start_time!);
  let endA = timeToMinutes(a.end_time!);
  const startB = timeToMinutes(b.start_time!);
  let endB = timeToMinutes(b.end_time!);
  if (endA < startA) endA += 1440;
  if (endB < startB) endB += 1440;
  return !(endA <= startB || endB <= startA);
};

const WEEK = 7 * 1440;

/**
 * Week-timeline intervals [start, end) for one schedule row: each selected
 * weekday ("0" = Sunday) places the window on that day, and a window whose
 * end is not after its start runs into the next day (Saturday wraps to
 * Sunday via the ±WEEK shift below).
 */
const weekIntervals = (schedule: Schedule): [number, number][] => {
  const start = timeToMinutes(schedule.start_time!);
  const end = timeToMinutes(schedule.end_time!);
  return schedule.day_of_week.map((day) => {
    const dayStart = Number(day) * 1440;
    return [dayStart + start, dayStart + (end > start ? end : end + 1440)];
  });
};

/** True overlap in time, including the after-midnight half (ENG-1970). */
export const schedulesOverlap = (a: Schedule, b: Schedule): boolean =>
  weekIntervals(a).some(([startA, endA]) =>
    weekIntervals(b).some(([startB, endB]) =>
      [-WEEK, 0, WEEK].some(
        (shift) => startA < endB + shift && startB + shift < endA
      )
    )
  );

const hasValidTimes = (schedule: Schedule) =>
  isValidTime(schedule.start_time) && isValidTime(schedule.end_time);

/** Pairs of flex schedules that share a product, with the shared products. */
const sharedProductPairs = (data: ProductSchedule[]) => {
  const valid = (data ?? []).filter(
    (entry) => entry && Array.isArray(entry.product)
  );
  const pairs: { a: ProductSchedule; b: ProductSchedule; shared: number[] }[] =
    [];
  for (let i = 0; i < valid.length; i++) {
    for (let j = i + 1; j < valid.length; j++) {
      const shared = valid[i].product.filter((product) =>
        valid[j].product.includes(product)
      );
      if (shared.length) pairs.push({ a: valid[i], b: valid[j], shared });
    }
  }
  return pairs;
};

const anyRows = (
  a: ProductSchedule,
  b: ProductSchedule,
  test: (x: Schedule, y: Schedule) => boolean
) =>
  (a.schedule ?? []).some(
    (rowA) =>
      hasValidTimes(rowA) &&
      (b.schedule ?? []).some((rowB) => hasValidTimes(rowB) && test(rowA, rowB))
  );

/**
 * Blocks saving: two schedules sharing a product overlap on the same day.
 * Unchanged rule; the overnight cases it misses are only warned about (see
 * findOvernightOnlySharedProductOverlaps), because ~100 existing sites are
 * already saved that way and must stay editable (ENG-1970).
 */
export const handleCheckOverlappingSchedulesForSharedProducts = (
  data: ProductSchedule[]
): boolean => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return false;
  }
  return sharedProductPairs(data).some(({ a, b }) =>
    anyRows(a, b, sameDayOverlap)
  );
};

/**
 * Warns before saving: schedules sharing a product that overlap only through
 * the after-midnight half of an overnight window, e.g. Intruder 22:00–06:00
 * and Intruder 05:00–07:00. Both connectors run that product then, so its
 * alerts arrive twice. Returns the shared product ids.
 */
export const findOvernightOnlySharedProductOverlaps = (
  data: ProductSchedule[]
): number[] => {
  const products = new Set<number>();
  sharedProductPairs(data).forEach(({ a, b, shared }) => {
    if (anyRows(a, b, sameDayOverlap)) return;
    if (anyRows(a, b, schedulesOverlap)) {
      shared.forEach((product) => products.add(product));
    }
  });
  return [...products];
};
